/**
 * Notification inbox admin API (Wave 2.33 FE, TASK-105 +
 * Wave 2.60 FE TASK-302-followup-fe-notifications-mark-read).
 *
 * Wire DTOs from `@music-app/shared`:
 *   - `NotificationInboxItem` / `NotificationInboxResponse`
 *   - `NotificationInboxFilter` (only `unread_only` is exposed by the drawer;
 *     `cursor` / `limit` use server defaults for v0.1.0)
 *   - `NotificationInboxMarkReadResponse` (`{ id, is_read, read_at }`)
 *   - `NotificationInboxMarkAllReadResponse` (`{ marked }`)
 *
 * Endpoints (`apps/api/src/notifications/notifications-inbox.controller.ts`):
 *   GET  /admin/notifications/inbox?unread_only=true|false   — list (Wave 2.32)
 *   POST /admin/notifications/inbox/:id/mark-read            — single (Wave 2.59)
 *   POST /admin/notifications/inbox/mark-all-read            — bulk   (Wave 2.59)
 *
 * Mark-read mutations are idempotent server-side. The per-row mutation
 * applies an optimistic patch to every cached page of `NOTIFICATIONS_INBOX_KEY`
 * (drawer + topbar badge share that key under different filter variants) so
 * the unread badge drops the moment the user clicks. On error we restore the
 * pre-mutation snapshot and let TanStack Query report. On settle we
 * invalidate to align with server truth (audit row, `read_at` from BE).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseMutationResult,
} from '@tanstack/react-query';
import type {
  NotificationInboxMarkAllReadResponse,
  NotificationInboxMarkReadResponse,
  NotificationInboxResponse,
} from '@music-app/shared';
import { apiClient } from '../lib/api-client';
import { STALE_TIME } from '../lib/query-client';

export const NOTIFICATIONS_INBOX_KEY = ['notifications', 'inbox'] as const;

/** Polling cadence for the open drawer — keeps the visible list fresh
 * while the user is reading. */
export const INBOX_DRAWER_REFETCH_MS = 30_000;

/** Polling cadence for the bell-icon badge while the drawer is closed.
 * Slower to save bandwidth — only the unread counter is observed. */
export const INBOX_BADGE_REFETCH_MS = 60_000;

export interface UseNotificationsInboxQueryOptions {
  /** When `true`, narrows the list to `is_read=false` rows. `unread_count`
   * in the response is always the total unread across all pages — it is
   * NOT affected by this flag. */
  unreadOnly?: boolean;
  /** Refetch interval in ms. Set per-call: drawer uses `INBOX_DRAWER_REFETCH_MS`
   * when open, the topbar uses `INBOX_BADGE_REFETCH_MS` when closed. */
  refetchIntervalMs?: number;
  /** When `false`, the query is disabled — used to pause the drawer query
   * when the drawer is closed. */
  enabled?: boolean;
}

export function useNotificationsInboxQuery(
  options: UseNotificationsInboxQueryOptions = {},
) {
  const {
    unreadOnly = false,
    refetchIntervalMs,
    enabled = true,
  } = options;

  return useQuery<NotificationInboxResponse, Error>({
    queryKey: [...NOTIFICATIONS_INBOX_KEY, { unreadOnly }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (unreadOnly) params.set('unread_only', 'true');
      const qs = params.toString();
      return apiClient<NotificationInboxResponse>(
        `/admin/notifications/inbox${qs ? `?${qs}` : ''}`,
      );
    },
    staleTime: STALE_TIME.liveCounter,
    refetchInterval: refetchIntervalMs,
    refetchIntervalInBackground: false,
    enabled,
  });
}

// -------------------------------------------------------------------------
// Mark-read mutations (Wave 2.60 FE, TASK-302-followup-fe-notifications-mark-read)
// -------------------------------------------------------------------------

/**
 * Snapshot of every inbox query's data captured before an optimistic patch
 * is applied. Used by `onError` rollback handlers.
 */
type InboxSnapshot = Array<[QueryKey, NotificationInboxResponse | undefined]>;

/**
 * Marks one inbox row as read.
 *
 * Optimistic: flips `is_read=true`, sets a synthetic `read_at` (now) on the
 * matching row in every cached page, decrements `unread_count` by 1 (clamped
 * at 0) only when the row was previously unread. Rollback on error restores
 * the captured snapshot. `onSettled` invalidates so the canonical `read_at`
 * from the server replaces the synthetic one.
 */
export function useMarkInboxReadMutation(): UseMutationResult<
  NotificationInboxMarkReadResponse,
  Error,
  { id: string },
  { snapshot: InboxSnapshot }
> {
  const queryClient = useQueryClient();
  return useMutation<
    NotificationInboxMarkReadResponse,
    Error,
    { id: string },
    { snapshot: InboxSnapshot }
  >({
    mutationFn: ({ id }) =>
      apiClient<NotificationInboxMarkReadResponse>(
        `/admin/notifications/inbox/${encodeURIComponent(id)}/mark-read`,
        { method: 'POST' },
      ),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_INBOX_KEY });
      const entries = queryClient.getQueriesData<NotificationInboxResponse>({
        queryKey: NOTIFICATIONS_INBOX_KEY,
      });
      const snapshot: InboxSnapshot = entries.map(([key, data]) => [key, data]);
      const nowIso = new Date().toISOString();
      for (const [key, data] of entries) {
        if (!data) continue;
        let flipped = false;
        const items = data.items.map((item) => {
          if (item.id !== id) return item;
          if (item.is_read) return item;
          flipped = true;
          return { ...item, is_read: true, read_at: nowIso };
        });
        if (!flipped) continue;
        queryClient.setQueryData<NotificationInboxResponse>(key, {
          ...data,
          items,
          unread_count: Math.max(0, data.unread_count - 1),
        });
      }
      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.snapshot) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_INBOX_KEY });
    },
  });
}

/**
 * Marks every inbox row owned by the caller as read.
 *
 * No optimistic patch: the user-visible signal lives in the button's
 * `loading` state and the subsequent invalidation refresh — flipping every
 * row locally would duplicate the server's count of `marked` rows on the
 * client side and complicate rollback on a partial failure. The endpoint is
 * cheap (a single UPDATE) and the drawer is already short.
 */
export function useMarkAllInboxReadMutation(): UseMutationResult<
  NotificationInboxMarkAllReadResponse,
  Error,
  void
> {
  const queryClient = useQueryClient();
  return useMutation<NotificationInboxMarkAllReadResponse, Error, void>({
    mutationFn: () =>
      apiClient<NotificationInboxMarkAllReadResponse>(
        '/admin/notifications/inbox/mark-all-read',
        { method: 'POST' },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: NOTIFICATIONS_INBOX_KEY,
      });
    },
  });
}
